import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Spinner } from '../Components/Icons'
import { useDocumentTitle } from '../Hooks/useDocumentTitle'
import { t } from '../i18n'
import { emailLinkIdToken, isEmailLink, loginErrorKind } from '../Services/login'
import { finishLogin } from '../State/Discussions'
import { useUi } from '../State/Ui'
import { track } from '../Utils/analytics'
import { KEYS, readJSON, removeKey } from '../Utils/storage'

/* /prijava: where the login email's link lands. Finishes the login, then
   goes back to the page (and comments) it was asked from. Opened in another
   browser (no saved email there), it asks for the address once. */
export default function Login() {
  const navigate = useNavigate()
  const { notify, openComments } = useUi()
  const [status, setStatus] = useState('checking') // checking | needEmail | working | invalid | error
  const [email, setEmail] = useState('')
  const started = useRef(false)
  useDocumentTitle(t('login.title'))

  const complete = useCallback(
    async (address) => {
      setStatus('working')
      try {
        await finishLogin(await emailLinkIdToken(address, window.location.href))
        const back = readJSON(KEYS.loginReturn, null)
        removeKey(KEYS.loginEmail)
        removeKey(KEYS.loginReturn)
        track('login', { method: 'email_link' })
        notify(t('login.welcome'))
        navigate(back?.path || '/', { replace: true })
        if (back?.storyId) openComments({ storyId: back.storyId, articleId: back.articleId })
      } catch (error) {
        setStatus(loginErrorKind(error) === 'expired' ? 'invalid' : 'error')
      }
    },
    [navigate, notify, openComments]
  )

  useEffect(() => {
    if (started.current) return
    started.current = true
    isEmailLink(window.location.href)
      .then((ok) => {
        if (!ok) return setStatus('invalid')
        const saved = readJSON(KEYS.loginEmail, '')
        return saved ? complete(saved) : setStatus('needEmail')
      })
      .catch(() => setStatus('error'))
  }, [complete])

  return (
    <div className="feed-message login-page">
      {(status === 'checking' || status === 'working') && (
        <>
          <Spinner size={24} />
          <p>{t('login.checking')}</p>
        </>
      )}
      {status === 'needEmail' && (
        <form
          className="login-page-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (email.trim()) complete(email.trim())
          }}
        >
          <p>{t('login.needEmail')}</p>
          <input
            className="name-input"
            type="email"
            value={email}
            placeholder={t('login.emailPlaceholder')}
            aria-label={t('login.emailLabel')}
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
          <button type="submit" className="name-continue" disabled={!email.trim()}>
            {t('comments.nameContinue')}
          </button>
        </form>
      )}
      {(status === 'invalid' || status === 'error') && (
        <>
          <p>{t(status === 'invalid' ? 'login.invalid' : 'login.errors.other')}</p>
          <Link to="/" className="btn btn-secondary">
            {t('notFound.home')}
          </Link>
        </>
      )}
    </div>
  )
}
